import { useLocation } from 'react-router-dom'
import { Crown } from 'iconoir-react'
import { type ReactElement, useEffect, useState } from 'react'
import { ChevronToggle } from 'app/components/ChevronToggle'
import { NavItem } from 'app/components/sidebar/NavItem'
import { NewPipeline } from 'app/components/sidebar/NewPipeline'
import { Button } from 'app/components/ui/button'
import type { useHomepage } from 'app/hooks/useHomepage'
import type { PipelineType } from 'app/types/entities'
import { titleCase } from 'app/utils/titleCase'
import type { NavItemType } from 'types/Sidebar'

export function NavGroup({
  item,
  homepage,
  pipelines,
  onPipelinesChanged,
}: {
  item: NavItemType
  homepage: ReturnType<typeof useHomepage>
  pipelines: PipelineType[]
  onPipelinesChanged: () => void
}): ReactElement {
  const location = useLocation()
  const hasActiveChild = !!item.children?.some((child) => child.active)
  const isActive = hasActiveChild || !!item.active
  const [open, setOpen] = useState(isActive)
  const Icon = item.icon

  // Navigation collapses every group back to the one holding the current page. isActive is
  // also a dependency, not just the URL, so a group whose active child arrives after the
  // initial render on the same URL (e.g. Pipelines before its list has loaded) still opens
  // once it does, instead of staying collapsed until the next navigation.
  useEffect(() => {
    setOpen(isActive)
  }, [location.pathname, location.search, isActive])

  return (
    <div className="flex flex-col">
      <Button
        variant="ghost"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="group h-9 w-full justify-start gap-x-2 pl-1.5 pr-1.75"
      >
        {/* Button shrinks any unsized svg to 16px with a :not() selector that outranks a plain descendant rule, so this needs !important; nav icons are 20px like every leaf row's. */}
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center text-default [&_svg]:!size-5">
            <Icon aria-hidden="true" />
          </span>
        )}
        <span className="min-w-0 grow truncate text-left text-heading-xs text-strong">{titleCase(item.name)}</span>
        {item.superUserOnly && (
          <span className="shrink-0 rounded-sm bg-amber-500/8 p-1">
            <Crown width={14} height={14} className="text-amber-800" />
          </span>
        )}
        <ChevronToggle
          open={open}
          className="hidden aspect-square h-6 items-center justify-center rounded-sm bg-surface p-0 group-hover:flex"
          chevronClassName="h-4 w-4 text-strong"
        />
      </Button>
      {open && (
        <div className="flex flex-col">
          {item.children?.map((child, index) =>
            child.divider ? (
              <div key={`divider-${index}`} className="my-2 ml-11.5 mr-2 border-t border-subtle" />
            ) : (
              <NavItem
                key={child.href ?? child.name}
                item={{ ...child, href: child.href ?? `#`, active: !!child.active }}
                isDefault={homepage.href === child.href}
                lockedDefault={homepage.isDefaultHomepage && homepage.href === child.href}
                pipeline={pipelines.find((pipeline) => child.href === `/pipelines?tab=${pipeline.id}`)}
                onSetAsDefault={homepage.setHomepage}
                onRemovePipeline={onPipelinesChanged}
              />
            ),
          )}
          {item.createPipeline && <NewPipeline onPipelineCreated={onPipelinesChanged} />}
        </div>
      )}
    </div>
  )
}
